/**
 * ConsentBadge.tsx — console-side mirror of the endpoint consent/presence state
 * (SPEC §11.7: consent/presence overlay shown for the entire session; user can
 * panic-disconnect). On the agent the overlay is unspoofable; here we surface
 * the technician's view of consent + a prominent disconnect affordance.
 */

export interface ConsentBadgeProps {
  granted: boolean | null; // null = awaiting consent
  remoteUser?: string;
  orgName?: string;
  onDisconnect: () => void;
}

export function ConsentBadge({ granted, remoteUser, orgName, onDisconnect }: ConsentBadgeProps) {
  const state =
    granted === null ? 'pending' : granted ? 'granted' : 'denied';
  const color =
    state === 'granted'
      ? 'border-helm-good/50 bg-helm-good/10 text-helm-good'
      : state === 'denied'
        ? 'border-helm-bad/50 bg-helm-bad/10 text-helm-bad'
        : 'border-helm-warn/50 bg-helm-warn/10 text-helm-warn';

  return (
    <div className={`flex items-center gap-3 rounded-md border px-3 py-1.5 text-sm ${color}`}>
      <span className="inline-flex items-center gap-2">
        <span
          className={`h-2 w-2 rounded-full ${
            state === 'granted'
              ? 'bg-helm-good'
              : state === 'denied'
                ? 'bg-helm-bad'
                : 'bg-helm-warn animate-pulse'
          }`}
          aria-hidden
        />
        {state === 'granted' && (
          <span>
            Consent granted{remoteUser ? ` by ${remoteUser}` : ''}
            {orgName ? ` · ${orgName}` : ''}
          </span>
        )}
        {state === 'pending' && <span>Awaiting end-user consent…</span>}
        {state === 'denied' && <span>Consent denied — session must end</span>}
      </span>
      <button
        type="button"
        onClick={onDisconnect}
        className="ml-auto rounded bg-helm-bad px-2 py-1 text-xs font-semibold text-white hover:opacity-90"
      >
        Disconnect
      </button>
    </div>
  );
}
